import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LangContext';
import { apartmentsApi, type ApartmentMember, type UpdateApartmentMemberData } from '../../api/apartmentsApi';

interface EditMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  member: ApartmentMember | null;
}

const EditMemberModal: React.FC<EditMemberModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  member,
}) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<UpdateApartmentMemberData>({
    fullName: '',
    dateOfBirth: '',
    relationshipCode: '',
    role: 'NHAN_KHAU',
    isPrimary: false,
    email: '',
    phone: '',
    citizenId: '',
  });

  useEffect(() => {
    if (member) {
      const dateOnly = member.dateOfBirth ? member.dateOfBirth.split('T')[0] : '';
      setFormData({
        fullName: member.fullName,
        dateOfBirth: dateOnly,
        relationshipCode: member.relationshipCode || '',
        role: member.role,
        isPrimary: member.isPrimary || false,
        email: member.email || '',
        phone: member.phone || '',
        citizenId: member.citizenId || '',
      });
    } else {
      setFormData({
        fullName: '',
        dateOfBirth: '',
        relationshipCode: '',
        role: 'NHAN_KHAU',
        isPrimary: false,
        email: '',
        phone: '',
        citizenId: '',
      });
    }
    setError(null);
  }, [member]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member) return;

    setError(null);

    if (!formData.fullName?.trim()) {
      setError(t('pleaseEnterFullName'));
      return;
    }

    try {
      setLoading(true);
      await apartmentsApi.updateApartmentMember(member.id, {
        fullName: formData.fullName.trim(),
        dateOfBirth: formData.dateOfBirth ? formData.dateOfBirth : null,
        relationshipCode: (formData.relationshipCode ?? '').trim() === '' ? null : formData.relationshipCode?.trim(),
        email: (formData.email ?? '').trim() === '' ? null : formData.email?.trim(),
        phone: (formData.phone ?? '').trim() === '' ? null : formData.phone?.trim(),
        citizenId: (formData.citizenId ?? '').trim() === '' ? null : formData.citizenId?.trim(),
        role: formData.role,
        isPrimary: formData.isPrimary,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(t('errorUpdatingMember'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{t('editMemberTitle')}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fullNameAndSurname')} *</label>
            <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('dateOfBirth')}</label>
            <input type="date" name="dateOfBirth" value={formData.dateOfBirth || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('residentRole')}</label>
            <select name="role" value={formData.role} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="CHU_HO">{t('residentRoleOwner')}</option>
              <option value="NHAN_KHAU">{t('residentRoleMember')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('relationshipRole')}</label>
            <input type="text" name="relationshipCode" value={formData.relationshipCode || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('email')}</label>
            <input type="text" name="email" value={formData.email || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('phone')}</label>
            <input type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('citizenId')}</label>
            <input type="text" name="citizenId" value={formData.citizenId || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-md">{t('cancel')}</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md disabled:opacity-50">
              {loading ? t('saving') : t('save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMemberModal;
